import React, { useState, useEffect } from 'react';
import { bulkAPI, categoryAPI, uploadAPI, API_BASE_URL } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { TableSkeleton } from '../../components/skeletons';
import { renderIcon } from '../admin/TaxonomyManager';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 20 },
  card: { padding: 18, borderRadius: 12, background: '#fff', border: '1px solid var(--border)', marginBottom: 12, transition: 'box-shadow 0.2s' },
  formCard: { padding: 24, borderRadius: 12, background: '#fff', border: '2px solid var(--green)', marginBottom: 20 },
  formTitle: { fontSize: 16, fontWeight: 700, color: 'var(--char)', marginBottom: 16 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  formRow3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--char)', marginBottom: 4 },
  input: { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box' },
  select: { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box', background: '#fff' },
  textarea: { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, resize: 'vertical', minHeight: 60, boxSizing: 'border-box' },
  tag: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, marginRight: 6 },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { background: '#fff', borderRadius: 16, padding: 28, width: 520, maxHeight: '80vh', overflowY: 'auto' },
  bidCard: { padding: 14, borderRadius: 10, border: '1px solid var(--border)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  emptyBox: { textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 12, border: '1px solid var(--border)' },
};

const formatIconText = (icon, fallback = '') => {
  if (!icon || typeof icon !== 'string') return fallback;
  if (icon.startsWith('http://') || icon.startsWith('https://') || icon.startsWith('/uploads/')) {
    return fallback;
  }
  return icon;
};

const EMPTY_FORM = { title: '', description: '', categoryId: '', subCategoryId: '', brandId: '', priceFloor: '', priceCeiling: '', stock: '', media: [], status: 'active' };
const API_BASE = API_BASE_URL;

const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url}`;
};

export default function BulkManager() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bidModal, setBidModal] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [infoModal, setInfoModal] = useState(null);

  // Edit & Details & Filter States
  const [editingId, setEditingId] = useState(null);
  const [detailListing, setDetailListing] = useState(null);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [errors, setErrors] = useState({ priceFloor: '', priceCeiling: '', stock: '' });

  const validateField = (name, value) => {
    let errMsg = '';
    if (name === 'priceFloor') {
      if (!value) {
        errMsg = 'Minimum price is required.';
      } else {
        const pf = Number(value);
        if (isNaN(pf) || pf <= 0) {
          errMsg = 'Minimum price must be a positive number.';
        }
      }
    } else if (name === 'priceCeiling') {
      if (!value) {
        errMsg = 'Maximum price is required.';
      } else {
        const pc = Number(value);
        if (isNaN(pc) || pc <= 0) {
          errMsg = 'Maximum price must be a positive number.';
        }
      }
    } else if (name === 'stock') {
      if (!value) {
        errMsg = 'Stock is required.';
      } else {
        const s = Number(value);
        if (isNaN(s) || s <= 0) {
          errMsg = 'Stock must be at least 1.';
        }
      }
    }
    setErrors(prev => ({ ...prev, [name]: errMsg }));
    return errMsg;
  };

  // Taxonomy
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const loadAll = () => {
    setLoading(true);
    bulkAPI.myListings()
      .then(r => setListings(r.data.data || []))
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    // Load taxonomy tree
    categoryAPI.tree()
      .then(r => {
        const tree = r.data.data || r.data || [];
        const cats = [], subs = [], brs = [];
        const subIdSet = new Set(), brIdSet = new Set();
        tree.forEach(c => {
          cats.push({ _id: c._id, name: c.name, icon: c.icon });
          (c.subcategories || c.subCategories || []).forEach(s => {
            if (!subIdSet.has(s._id)) {
              subIdSet.add(s._id);
              subs.push({ _id: s._id, name: s.name, icon: s.icon, categoryIds: (s.categories || []).map(x => String(x._id || x)) });
            }
            (s.brands || []).forEach(b => {
              if (!brIdSet.has(b._id)) {
                brIdSet.add(b._id);
                brs.push({ _id: b._id, name: b.name, icon: b.icon, subCategoryIds: (b.subCategories || []).map(x => String(x._id || x)) });
              }
            });
          });
        });
        setCategories(cats);
        setSubCategories(subs);
        setBrands(brs);
      })
      .catch(() => { });
  }, []);

  const handleChange = (field, val) => {
    const updated = { ...form, [field]: val };
    if (field === 'categoryId') { updated.subCategoryId = ''; updated.brandId = ''; }
    if (field === 'subCategoryId') { updated.brandId = ''; }
    setForm(updated);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const res = await uploadAPI.upload(files);
      const newMedia = (res.data.data || []).map((m, i) => ({
        url: m.url,
        type: m.type,
        isDefault: form.media.length === 0 && i === 0,
      }));
      setForm(f => ({ ...f, media: [...f.media, ...newMedia] }));
    } catch (err) { console.error('Upload error:', err); alert('Upload failed: ' + (err.response?.data?.message || err.message)); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const removeMedia = (idx) => {
    setForm(f => {
      const media = f.media.filter((_, i) => i !== idx);
      if (media.length > 0 && !media.some(m => m.isDefault)) media[0].isDefault = true;
      return { ...f, media };
    });
  };

  const setDefault = (idx) => {
    setForm(f => ({ ...f, media: f.media.map((m, i) => ({ ...m, isDefault: i === idx })) }));
  };

  const filteredSubs = form.categoryId
    ? subCategories.filter(s => s.categoryIds.includes(String(form.categoryId)))
    : [];
  const filteredBrands = form.subCategoryId
    ? brands.filter(b => b.subCategoryIds.includes(String(form.subCategoryId)))
    : [];

  const handleAddSKU = async () => {
    if (!form.title || !form.stock || !form.categoryId) return;
    const errPf = validateField('priceFloor', form.priceFloor);
    const errPc = validateField('priceCeiling', form.priceCeiling);
    const errS = validateField('stock', form.stock);

    if (errPf || errPc || errS) return;

    if (Number(form.priceCeiling) < Number(form.priceFloor)) {
      setErrors(prev => ({ ...prev, priceCeiling: 'Maximum price cannot be less than minimum price.' }));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        categoryId: form.categoryId,
        subCategoryId: form.subCategoryId || undefined,
        brandId: form.brandId || undefined,
        priceFloor: Number(form.priceFloor) || 0,
        priceCeiling: Number(form.priceCeiling) || 0,
        stock: Number(form.stock),
        media: form.media,
        status: form.status || 'active',
      };

      if (editingId) {
        const res = await bulkAPI.updateListing(editingId, payload);
        // Refresh details modal local states if viewing updated listing
        if (detailListing?._id === editingId) {
          setDetailListing(res.data.data);
        }
        setListings(listings.map(l => l._id === editingId ? res.data.data : l));
        setEditingId(null);
      } else {
        const res = await bulkAPI.createListing(payload);
        setListings([res.data.data, ...listings]);
      }
      setForm({ ...EMPTY_FORM });
      setErrors({ priceFloor: '', priceCeiling: '', stock: '' });
      setShowForm(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save listing');
    } finally { setSaving(false); }
  };

  const handleEditClick = (l) => {
    setEditingId(l._id);
    setErrors({ priceFloor: '', priceCeiling: '', stock: '' });
    setForm({
      title: l.title || '',
      description: l.description || '',
      categoryId: l.categoryId?._id || l.categoryId || '',
      subCategoryId: l.subCategoryId?._id || l.subCategoryId || '',
      brandId: l.brandId?._id || l.brandId || '',
      priceFloor: l.priceFloor || '',
      priceCeiling: l.priceCeiling || '',
      stock: l.stock || '',
      media: l.media || [],
      status: l.status || 'active',
    });
    setShowForm(true);
    setDetailListing(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this listing?")) {
      try {
        await bulkAPI.removeListing(id);
        setListings(listings.filter(l => l._id !== id));
        if (detailListing?._id === id) setDetailListing(null);
      } catch (err) {
        alert("Failed to delete listing.");
      }
    }
  };

  const openBids = async (listingId) => {
    setBidModal(listingId);
    setBidsLoading(true);
    try {
      const res = await bulkAPI.bidsByListing(listingId);
      setBids(res.data.data || []);
    } catch (err) { setBids([]); }
    finally { setBidsLoading(false); }
  };

  const handleBidAction = async (bidId, action) => {
    try {
      if (action === 'accept') {
        await bulkAPI.acceptBid(bidId);
        setInfoModal("We have sent your contact details to the buyer. They will contact you.");
        setBids(bids.map(b => b._id === bidId ? { ...b, status: 'accepted', isRepeatBid: true } : b));
      } else {
        await bulkAPI.rejectBid(bidId);
        setBids(bids.map(b => b._id === bidId ? { ...b, status: 'rejected' } : b));
      }
    } catch (err) { alert(err.response?.data?.message || 'Failed to update bid'); }
  };

  const getSubCategoryName = (id) => {
    if (!id) return '';
    if (typeof id === 'object') return id.name;
    const found = subCategories.find(s => s._id === id);
    return found ? found.name : id;
  };

  const getBrandName = (id) => {
    if (!id) return '';
    if (typeof id === 'object') return id.name;
    const found = brands.find(b => b._id === id);
    return found ? found.name : id;
  };

  const getBrandBadge = (id) => {
    if (!id) return null;
    let b = typeof id === 'object' ? id : brands.find(x => String(x._id) === String(id));
    const name = b ? b.name : (typeof id === 'string' ? id : '');
    const icon = b ? b.icon : (typeof id === 'object' ? id.icon : null);
    return (
      <span style={{ ...S.tag, background: '#FFF8E7', color: '#92400E', display: 'inline-flex', alignItems: 'center' }}>
        {renderIcon(icon, { width: 14, height: 14, marginRight: 4 })}
        {name}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Active', color: '#10b981', bg: '#ecfdf5' },
      paused: { label: 'Paused', color: '#f59e0b', bg: '#fffbeb' },
      sold_out: { label: 'Sold Out', color: '#ef4444', bg: '#fef2f2' },
      expired: { label: 'Expired', color: '#374151', bg: '#f3f4f6' },
    };
    const current = config[status] || config.active;
    return (
      <span style={{
        ...S.tag,
        background: current.bg,
        color: current.color,
        border: `1px solid ${current.color}33`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: current.color }}></span>
        {current.label}
      </span>
    );
  };

  // Status Filtering Pill Counts
  const counts = {
    all: listings.length,
    active: listings.filter(l => l.status === 'active').length,
    paused: listings.filter(l => l.status === 'paused').length,
    sold_out: listings.filter(l => l.status === 'sold_out').length,
    expired: listings.filter(l => l.status === 'expired').length,
  };

  const filteredListings = statusFilter === 'all'
    ? listings
    : listings.filter(l => l.status === statusFilter);

  if (loading) {
    return (
      <div className="bulk-container">
        <div style={S.title}>Bulk Sales Inventory</div>
        <div style={S.subtitle}>Manage your wholesale & clearance inventory, view bids, and update stock.</div>
        <TableSkeleton rows={5} columns={5} />
      </div>
    );
  }

  return (
    <div className="bulk-container">
      <div className="bulk-header">
        <div>
          <div style={S.title}>Bulk Manager</div>
          <div style={S.subtitle}>Manage your bulk listings and bids</div>
        </div>
        <button onClick={() => {
          setErrors({ priceFloor: '', priceCeiling: '', stock: '' });
          if (showForm) {
            setShowForm(false);
            setEditingId(null);
            setForm({ ...EMPTY_FORM });
          } else {
            setShowForm(true);
            setEditingId(null);
            setForm({ ...EMPTY_FORM });
          }
        }}
          style={{ background: showForm ? 'var(--muted)' : 'var(--green)', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {showForm ? 'Cancel' : '+ Add SKU'}
        </button>
      </div>

      {/* Add / Edit SKU Form */}
      {showForm && (
        <div style={S.formCard}>
          <div style={S.formTitle}>{editingId ? '✏️ Edit Listing' : '📦 Add New Listing'}</div>

          {/* Category → SubCategory → Brand */}
          <div className="bulk-form-grid-3">
            <div>
              <label style={S.label}>Category *</label>
              <select style={S.select} value={form.categoryId} onChange={e => handleChange('categoryId', e.target.value)}>
                <option value="">Select category</option>
                {categories.map(c => <option key={c._id} value={c._id}>{formatIconText(c.icon)} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Sub Category</label>
              <select style={S.select} value={form.subCategoryId} onChange={e => handleChange('subCategoryId', e.target.value)} disabled={!form.categoryId}>
                <option value="">{form.categoryId ? 'Select sub category' : 'Select category first'}</option>
                {filteredSubs.map(s => <option key={s._id} value={s._id}>{formatIconText(s.icon)} {s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Brand</label>
              <select style={S.select} value={form.brandId} onChange={e => handleChange('brandId', e.target.value)} disabled={!form.subCategoryId}>
                <option value="">{form.subCategoryId ? 'Select brand' : 'Select sub category first'}</option>
                {filteredBrands.map(b => <option key={b._id} value={b._id}>{formatIconText(b.icon)} {b.name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Product Name *</label>
            <input style={S.input} className="form-input" placeholder="e.g. Samsung Galaxy S24 Ultra" value={form.title} onChange={e => handleChange('title', e.target.value)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Description</label>
            <textarea style={S.textarea} className="form-input" placeholder="Describe the product, specs, condition..." value={form.description} onChange={e => handleChange('description', e.target.value)} />
          </div>

          {/* Media Upload */}
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Images & Videos</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              {form.media.map((m, i) => (
                <div key={i} style={{ position: 'relative', width: 90, height: 90, borderRadius: 10, overflow: 'hidden', border: m.isDefault ? '2px solid var(--green)' : '1px solid var(--border)' }}>
                  {m.type === 'video' ? (
                    <video src={getMediaUrl(m.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                  ) : (
                    <img src={getMediaUrl(m.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  {m.isDefault && <div style={{ position: 'absolute', top: 4, left: 4, background: 'var(--green)', color: '#fff', fontSize: 8, fontWeight: 800, padding: '2px 5px', borderRadius: 4 }}>DEFAULT</div>}
                  {m.type === 'video' && <div style={{ position: 'absolute', bottom: 4, left: 4, background: '#000', color: '#fff', fontSize: 8, fontWeight: 700, padding: '2px 5px', borderRadius: 4 }}>▶ Video</div>}
                  <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 3 }}>
                    {!m.isDefault && <button onClick={() => setDefault(i)} title="Set as default" style={{ background: '#fff', border: 'none', borderRadius: 4, width: 20, height: 20, cursor: 'pointer', fontSize: 10, lineHeight: '20px' }}>★</button>}
                    <button onClick={() => removeMedia(i)} title="Remove" style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, width: 20, height: 20, cursor: 'pointer', fontSize: 10, lineHeight: '20px' }}>✕</button>
                  </div>
                </div>
              ))}
              <label style={{ width: 90, height: 90, borderRadius: 10, border: '2px dashed var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', background: '#fafafa', opacity: uploading ? 0.5 : 1 }}>
                <input type="file" multiple accept="image/*,video/*" onChange={handleFileUpload} disabled={uploading} style={{ display: 'none' }} />
                <span style={{ fontSize: 22, lineHeight: 1 }}>{uploading ? '⏳' : '📷'}</span>
                <span style={{ fontSize: 9, color: 'var(--muted)', marginTop: 4 }}>{uploading ? 'Uploading...' : 'Add Media'}</span>
              </label>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>
              {form.media.length === 0 ? 'No media added yet. First image becomes the default cover.' : `${form.media.length} file${form.media.length > 1 ? 's' : ''} · Click ★ to set default cover`}
            </div>
          </div>

          <div className="bulk-form-grid-2">
            <div>
              <label style={S.label}>Min Price (₹) *</label>
              <input style={{ ...S.input, borderColor: errors.priceFloor ? '#ef4444' : undefined }} className="form-input" type="number" placeholder="0" value={form.priceFloor} onChange={e => { handleChange('priceFloor', e.target.value); validateField('priceFloor', e.target.value); }} onWheel={(e) => e.target.blur()} />
              {errors.priceFloor && <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4, fontWeight: 500 }}>{errors.priceFloor}</div>}
            </div>
            <div>
              <label style={S.label}>Max Price (₹) *</label>
              <input style={{ ...S.input, borderColor: errors.priceCeiling ? '#ef4444' : undefined }} className="form-input" type="number" placeholder="0" value={form.priceCeiling} onChange={e => { handleChange('priceCeiling', e.target.value); validateField('priceCeiling', e.target.value); }} onWheel={(e) => e.target.blur()} />
              {errors.priceCeiling && <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4, fontWeight: 500 }}>{errors.priceCeiling}</div>}
            </div>
          </div>

          <div className="bulk-form-grid-2">
            <div>
              <label style={S.label}>Stock Quantity *</label>
              <input style={{ ...S.input, borderColor: errors.stock ? '#ef4444' : undefined }} className="form-input" type="number" placeholder="0" value={form.stock} onChange={e => { handleChange('stock', e.target.value); validateField('stock', e.target.value); }} onWheel={(e) => e.target.blur()} />
              {errors.stock && <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4, fontWeight: 500 }}>{errors.stock}</div>}
            </div>
            <div>
              <label style={S.label}>Listing Status</label>
              <select style={S.select} value={form.status} onChange={e => handleChange('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="sold_out">Sold Out</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          <button onClick={handleAddSKU} disabled={saving || !form.title || !form.stock || !form.categoryId}
            style={{ background: 'var(--green)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: (saving || !form.categoryId) ? 0.6 : 1, marginTop: 12 }}>
            {saving ? 'Saving...' : editingId ? 'Update Listing' : 'Add Listing'}
          </button>
        </div>
      )}

      {/* Status Filter Pills */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'All', color: '#6b7280', bg: '#f3f4f6' },
          { key: 'active', label: 'Active', color: '#10b981', bg: '#ecfdf5' },
          { key: 'paused', label: 'Paused', color: '#f59e0b', bg: '#fffbeb' },
          { key: 'sold_out', label: 'Sold Out', color: '#ef4444', bg: '#fef2f2' },
          { key: 'expired', label: 'Expired', color: '#374151', bg: '#f9fafb' },
        ].map(pill => {
          const isActive = statusFilter === pill.key;
          return (
            <button
              key={pill.key}
              onClick={() => setStatusFilter(pill.key)}
              style={{
                background: isActive ? pill.color : '#fff',
                color: isActive ? '#fff' : 'var(--char)',
                border: isActive ? `1px solid ${pill.color}` : '1px solid var(--border)',
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span>{pill.label}</span>
              <span style={{
                background: isActive ? 'rgba(255,255,255,0.2)' : pill.bg,
                color: isActive ? '#fff' : pill.color,
                padding: '2px 6px',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 700
              }}>
                {counts[pill.key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Listing Cards */}
      {filteredListings.length === 0 && !showForm ? (
        <div style={S.emptyBox}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📦</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No bulk listings in this tab</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Adjust your status filters above or add a new SKU</div>
        </div>
      ) : (
        filteredListings.map(l => (
          <div key={l._id} style={{ ...S.card, cursor: 'pointer' }} onClick={() => { setDetailListing(l); setGalleryIdx(0); }}>
            <div className="bulk-card-content">
              <div className="bulk-card-body">
                {/* Default image thumbnail */}
                {(() => {
                  const def = l.media?.find(m => m.isDefault) || l.media?.[0];
                  return def ? (
                    <div style={{ width: 72, height: 72, borderRadius: 10, overflow: 'hidden', flexShrink: 0, border: '1px solid var(--border)' }}>
                      {def.type === 'video'
                        ? <video src={getMediaUrl(def.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                        : <img src={getMediaUrl(def.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      }
                    </div>
                  ) : (
                    <div style={{ width: 72, height: 72, borderRadius: 10, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 28 }}>📦</div>
                  );
                })()}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)', marginBottom: 4 }}>{l.title || l.product}</div>
                  {/* Category / SubCategory / Brand badges */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {getStatusBadge(l.status)}
                    {l.categoryId && (
                      <span style={{ ...S.tag, background: '#E8F5EE', color: '#1B5C3A' }}>
                        {formatIconText(l.categoryId.icon, '📁')} {l.categoryId.name || l.categoryId}
                      </span>
                    )}
                    {l.subCategoryId && (
                      <span style={{ ...S.tag, background: '#EEF2FF', color: '#4338CA' }}>
                        {getSubCategoryName(l.subCategoryId)}
                      </span>
                    )}
                    {l.brandId && getBrandBadge(l.brandId)}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--muted)', flexWrap: 'wrap' }}>
                    <span>Price: ₹{(l.priceFloor || l.priceMin || 0).toLocaleString('en-IN')} - ₹{(l.priceCeiling || l.priceMax || 0).toLocaleString('en-IN')}</span>
                    <span style={{ ...S.tag, background: '#E8F5EE', color: '#1B5C3A' }}>Stock: {l.stock || 0}</span>
                    <span style={{ ...S.tag, background: '#FFF8E7', color: '#F5A623' }}>Sold: {l.sold || 0}</span>
                    <span style={{ ...S.tag, background: '#F3F4F6', color: 'var(--char)' }}>Bids: {l.bidsCount || l.bidCount || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bulk-card-actions">
                <button onClick={(e) => { e.stopPropagation(); openBids(l._id); }}
                  style={{ background: 'var(--green)', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  View Bids
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleEditClick(l); }}
                  style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Edit
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleDelete(l._id); }}
                  style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Detail Modal */}
      {detailListing && (() => {
        const media = detailListing.media || [];
        const current = media[galleryIdx];
        return (
          <div onClick={() => setDetailListing(null)} style={S.modal}>
            <div onClick={e => e.stopPropagation()} className="bulk-detail-modal-content">
              {/* Gallery */}
              {media.length > 0 ? (
                <div style={{ position: 'relative', background: '#111', borderRadius: '16px 16px 0 0', overflow: 'hidden' }}>
                  <div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {current?.type === 'video'
                      ? <video src={getMediaUrl(current.url)} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%' }} />
                      : current ? <img src={getMediaUrl(current.url)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : null}
                  </div>
                  {media.length > 1 && <>
                    <button onClick={() => setGalleryIdx(i => (i - 1 + media.length) % media.length)}
                      style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 34, height: 34, fontSize: 18, cursor: 'pointer', fontWeight: 'bold' }}>‹</button>
                    <button onClick={() => setGalleryIdx(i => (i + 1) % media.length)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 34, height: 34, fontSize: 18, cursor: 'pointer', fontWeight: 'bold' }}>›</button>
                  </>}
                  <span style={{ position: 'absolute', bottom: 8, right: 12, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8 }}>{galleryIdx + 1} / {media.length}</span>
                  <button onClick={() => setDetailListing(null)} style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 30, height: 30, fontSize: 15, cursor: 'pointer' }}>✕</button>
                </div>
              ) : (
                <div style={{ height: 180, background: '#F3F4F6', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56 }}>📦</div>
              )}

              {media.length > 1 && (
                <div style={{ display: 'flex', gap: 5, padding: '8px 16px', overflowX: 'auto', background: '#fafafa' }}>
                  {media.map((m, i) => (
                    <div key={i} onClick={() => setGalleryIdx(i)}
                      style={{ width: 50, height: 50, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', border: i === galleryIdx ? '2px solid var(--green)' : '1px solid var(--border)', flexShrink: 0, opacity: i === galleryIdx ? 1 : 0.5 }}>
                      {m.type === 'video'
                        ? <video src={getMediaUrl(m.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                        : <img src={getMediaUrl(m.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ padding: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: 'var(--char)', margin: '0 0 8px' }}>{detailListing.title}</h2>
                <div style={{ display: 'flex', gap: 5, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                  {getStatusBadge(detailListing.status)}
                  {detailListing.categoryId && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: '#E8F5EE', color: '#1B5C3A' }}>{formatIconText(detailListing.categoryId.icon, '📁')} {detailListing.categoryId.name || detailListing.categoryId}</span>}
                  {detailListing.subCategoryId && <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: '#EEF2FF', color: '#4338CA' }}>{getSubCategoryName(detailListing.subCategoryId)}</span>}
                  {detailListing.brandId && getBrandBadge(detailListing.brandId)}
                </div>

                {detailListing.description && <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 16 }}>{detailListing.description}</p>}

                <div className="bulk-detail-modal-grid-3">
                  <div style={{ background: '#F0FDF4', borderRadius: 10, padding: 10, textAlign: 'center', border: '1px solid #DCFCE7' }}>
                    <div style={{ fontSize: 10, color: '#166534', fontWeight: 600 }}>Price Floor</div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: '#1B5C3A', marginTop: 2 }}>₹{(detailListing.priceFloor || 0).toLocaleString('en-IN')}</div>
                  </div>
                  <div style={{ background: '#FDF2F2', borderRadius: 10, padding: 10, textAlign: 'center', border: '1px solid #FEE2E2' }}>
                    <div style={{ fontSize: 10, color: '#991B1B', fontWeight: 600 }}>Price Ceiling</div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: '#DC2626', marginTop: 2 }}>₹{(detailListing.priceCeiling || 0).toLocaleString('en-IN')}</div>
                  </div>
                  <div style={{ background: '#F9FAFB', borderRadius: 10, padding: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>Stock Available</div>
                    <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>{detailListing.stock - (detailListing.sold || 0)} / {detailListing.stock} units</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, borderTop: '1px solid var(--border)', paddingTop: 16, justifyContent: 'flex-end' }}>
                  <button onClick={() => handleEditClick(detailListing)}
                    style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Edit SKU
                  </button>
                  <button onClick={() => handleDelete(detailListing._id)}
                    style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Delete
                  </button>
                  <button onClick={() => setDetailListing(null)}
                    style={{ background: 'var(--muted)', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bids Modal */}
      {bidModal && (
        <div style={S.modal} onClick={() => setBidModal(null)}>
          <div className="bulk-modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--char)' }}>Bids</div>
              <button onClick={() => setBidModal(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
            </div>
            {bidsLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted)', fontSize: 13 }}>Loading bids...</div>
            ) : bids.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted)', fontSize: 13 }}>No bids yet for this listing</div>
            ) : (
              bids.map(b => (
                <div key={b._id} style={{ ...S.bidCard, flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)' }}>{b.buyerId?.fullName || 'Buyer'}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                      Bid: ₹{(b.bidPrice || 0).toLocaleString('en-IN')} | Qty: {b.qty || 1}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                      {b.buyerId?.city || ''} · {new Date(b.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </div>
                    {b.isRepeatBid && (
                      <div style={{ fontSize: 11, color: '#d97706', fontWeight: 600, marginTop: 4 }}>
                        ⚠️ Repeat bid from same buyer
                      </div>
                    )}
                  </div>
                  {b.status === 'pending' ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleBidAction(b._id, 'accept')}
                        style={{ background: '#1B5C3A', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        Accept
                      </button>
                      <button onClick={() => handleBidAction(b._id, 'reject')}
                        style={{ background: '#DC2626', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        Reject
                      </button>
                    </div>
                  ) : (
                    <span style={{
                      padding: '4px 12px', borderRadius: 14, fontSize: 11, fontWeight: 700,
                      background: b.status === 'accepted' ? '#E8F5EE' : '#FEE2E2',
                      color: b.status === 'accepted' ? '#1B5C3A' : '#DC2626',
                    }}>
                      {b.status}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {/* Info/Success Message Modal */}
      {infoModal && (
        <div style={S.modal} onClick={() => setInfoModal(null)}>
          <div style={{ ...S.modalContent, width: 420, textAlign: 'center', padding: '36px 28px' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>📩</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--char)', marginBottom: 12 }}>Details Sent</div>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 24px' }}>
              {infoModal}
            </p>
            <button
              onClick={() => setInfoModal(null)}
              style={{ background: 'var(--green)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%', transition: 'opacity 0.2s' }}
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
